export const ETIQUETA_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const ETIQUETA_NIVELES = ['NORMAL', 'POCO_IMPORTANTE', 'MUY_IMPORTANTE', 'SUPER_IMPORTANTE'] as const;

export type EtiquetaNivel = (typeof ETIQUETA_NIVELES)[number];

export const ETIQUETA_NIVEL_LABELS: Record<EtiquetaNivel, string> = {
  NORMAL: 'Normal',
  POCO_IMPORTANTE: 'Poco importante',
  MUY_IMPORTANTE: 'Muy importante',
  SUPER_IMPORTANTE: 'Super importante',
};

export function etiquetaNivelLabel(nivel?: string | null): string {
  if (nivel && nivel in ETIQUETA_NIVEL_LABELS) {
    return ETIQUETA_NIVEL_LABELS[nivel as EtiquetaNivel];
  }
  return ETIQUETA_NIVEL_LABELS.NORMAL;
}

export type EtiquetaFormVista = 'normal' | 'avanzada';

export interface EtiquetaConfiguracionPayload {
  aplicarScript: boolean;
  script: string;
  scriptTests: string;
  scriptValido: boolean;
}

export interface EtiquetaFormValues {
  id: string;
  nombre: string;
  descripcion: string;
  color: string;
  habilitado: string;
  nivel: string;
  aplicarScript: string;
  script: string;
  scriptTests: string;
  scriptValido: string;
}

export const ETIQUETA_FORM_FIELD_NAMES = [
  'id',
  'nombre',
  'descripcion',
  'nivel',
  'color',
  'habilitado',
  'aplicarScript',
  'script',
  'scriptTests',
  'scriptValido',
] as const;

const AVANZADA_ONLY = new Set(['script', 'scriptTests', 'scriptValido']);

export function etiquetaVisibleFieldNames(vista: EtiquetaFormVista): string[] {
  if (vista === 'avanzada') {
    return [...ETIQUETA_FORM_FIELD_NAMES];
  }
  return ETIQUETA_FORM_FIELD_NAMES.filter(name => !AVANZADA_ONLY.has(name));
}

export function shouldShowEtiquetaValidarButton(vista: EtiquetaFormVista): boolean {
  return vista === 'avanzada';
}

export function allEtiquetaTestsPassed(report?: { results?: Array<{ passed?: boolean }> | null } | null): boolean {
  const results = report?.results ?? [];
  return results.length > 0 && results.every(item => item.passed === true);
}

export type EtiquetaConsoleLineKind = 'ok' | 'fail' | 'info' | 'error' | 'phase';

export const ETIQUETA_CONSOLE_MSG = {
  generando: 'Generando Scripts',
  ejecutando: 'Ejecutando Tests',
  resultado: 'Resultado de Tests',
  errorGenerar: 'Error al generar Scripts',
  errorTesting: 'Error de Testing',
  exito: '✅ Script Generado con éxito y 100% de los Tests Aprobados',
} as const;

export type EtiquetaConsolePhase = keyof typeof ETIQUETA_CONSOLE_MSG;

export type EtiquetaConsoleLine = {
  kind: EtiquetaConsoleLineKind;
  text: string;
};

export function etiquetaConsoleLines(report?: {
  messages?: Array<{ level?: string; text?: string }> | null;
  results?: Array<{ testDescription?: string; passed?: boolean; description?: string }> | null;
} | null): EtiquetaConsoleLine[] {
  if (!report) {
    return [];
  }
  const lines: EtiquetaConsoleLine[] = [];
  (report.messages ?? []).forEach(msg => {
    const level = (msg.level || 'INFO').toUpperCase();
    const text = [level, msg.text].filter(Boolean).join(' ');
    if (!text) {
      return;
    }
    lines.push({
      kind: level === 'ERROR' || level === 'FAIL' ? 'error' : 'info',
      text,
    });
  });
  (report.results ?? []).forEach(item => {
    const name = item.testDescription || 'Test';
    const detail = item.description ? ` — ${item.description}` : '';
    lines.push({
      kind: item.passed ? 'ok' : 'fail',
      text: `${item.passed ? 'PASS' : 'FAIL'} ${name}${detail}`,
    });
  });
  return lines;
}

export function etiquetaConsolePhaseLines(
  phase: EtiquetaConsolePhase,
  extra?: {
    report?: {
      messages?: Array<{ level?: string; text?: string }> | null;
      results?: Array<{ testDescription?: string; passed?: boolean; description?: string }> | null;
    } | null;
    detail?: string;
  },
): EtiquetaConsoleLine[] {
  const kind: EtiquetaConsoleLineKind = phase === 'errorGenerar' || phase === 'errorTesting' ? 'error' : 'phase';
  const lines: EtiquetaConsoleLine[] = [{ kind, text: ETIQUETA_CONSOLE_MSG[phase] }];
  if (phase === 'resultado') {
    lines.push(...etiquetaConsoleLines(extra?.report));
    if (allEtiquetaTestsPassed(extra?.report)) {
      lines.push({ kind: 'ok', text: ETIQUETA_CONSOLE_MSG.exito });
    }
  }
  const detail = extra?.detail?.trim();
  if (detail && (phase === 'errorGenerar' || phase === 'errorTesting')) {
    lines.push({ kind: 'error', text: detail });
  }
  return lines;
}

export function validateEtiquetaForm(
  form: Pick<EtiquetaFormValues, 'nombre' | 'descripcion' | 'color' | 'habilitado' | 'nivel' | 'aplicarScript' | 'script' | 'scriptTests' | 'scriptValido'>,
): Partial<Record<keyof EtiquetaFormValues, string>> {
  const errors: Partial<Record<keyof EtiquetaFormValues, string>> = {};
  if (!form.nombre.trim()) {
    errors.nombre = 'El nombre es requerido';
  }
  const color = form.color.trim();
  if (color && !ETIQUETA_COLOR_PATTERN.test(color)) {
    errors.color = 'Usa un color hexadecimal (#RRGGBB)';
  }
  const nivel = form.nivel.trim();
  if (nivel && !ETIQUETA_NIVELES.includes(nivel as EtiquetaNivel)) {
    errors.nivel = 'Nivel inválido';
  }
  return errors;
}

export function toEtiquetaPayload(form: EtiquetaFormValues): {
  id?: number;
  nombre: string;
  descripcion: string | null;
  color: string | null;
  habilitado: boolean;
  nivel: EtiquetaNivel;
  configuracion: EtiquetaConfiguracionPayload;
} {
  const descripcion = form.descripcion.trim();
  const color = form.color.trim();
  const nivel = form.nivel.trim();
  return {
    ...(form.id ? { id: Number(form.id) } : {}),
    nombre: form.nombre.trim(),
    descripcion: descripcion || null,
    color: color || null,
    habilitado: form.habilitado === 'true',
    nivel: (ETIQUETA_NIVELES.includes(nivel as EtiquetaNivel) ? nivel : 'NORMAL') as EtiquetaNivel,
    configuracion: {
      aplicarScript: form.aplicarScript === 'true',
      script: form.script,
      scriptTests: form.scriptTests,
      scriptValido: form.scriptValido === 'true',
    },
  };
}

export function configuracionToForm(configuracion?: Partial<EtiquetaConfiguracionPayload> | null): Pick<
  EtiquetaFormValues,
  'aplicarScript' | 'script' | 'scriptTests' | 'scriptValido'
> {
  return {
    aplicarScript: String(configuracion?.aplicarScript ?? false),
    script: configuracion?.script ?? '',
    scriptTests: configuracion?.scriptTests ?? '',
    scriptValido: String(configuracion?.scriptValido ?? false),
  };
}
